/** Platform admin: plans & pricing screen (P9, mockups/admin/plans.html). fil mirrors every key. */
export const plans = {
  title: "Plans & pricing",
  subtitle: "Plan limits, price books per currency (PHP, USD), add-ons, coupons, tax rules and payment gateways. Prices exclude tax (PH 12% VAT added on invoices; other countries zero-rated).",
  cycle: { label: "Billing cycle", monthly: "Monthly", annual: "Annual (−17%)" },
  previewPricing: "Preview pricing page",
  newPlan: "New plan",
  emptyTitle: "No plans yet",
  emptyBody: "Create the first plan to start selling workspaces.",
  card: {
    pricesCaption: "{{plan}} prices per currency",
    nextPrice: "{{price}} from {{date}}",
    addPrice: "Add price",
    addPriceLabel: "Add a price for {{plan}}",
    subs_one: "{{count}} sub",
    subs_other: "{{count}} subs",
    popular: "Most popular",
    draft: "Draft · hidden",
    custom: "Custom",
    customMonthly: " · quoted per contract",
    customAnnual: " · annual contract",
    messages: "Messages",
    agents: "Agents",
    storage: "Storage",
    seats: "Seats",
    fileSize: "File size",
    models: "Models",
    mb: "{{value}} MB",
    featuresLabel: "Features on {{plan}}",
    featureOn: "{{feature}} on {{plan}}",
    featureAdded: "{{feature}} added to {{plan}}",
    featureRemoved: "{{feature}} removed from {{plan}}",
    edit: "Edit plan",
    editLabel: "Edit plan {{plan}}",
    subscribers: "Subscribers",
    subscribersOf: "Subscribers of {{plan}}",
  },
  tiers: { standard: "Standard", premium: "Premium", standardHint: "Fast, low-cost chat models", premiumHint: "Highest-quality chat models" },
  features: {
    widget: "Website widget",
    webapp: "Web app + custom domain",
    messenger: "Messenger channel",
    api: "API access",
    actions: "Actions (booking, webhooks)",
    helpdesk: "Helpdesk & handoff",
    mobile_white_label: "White-label mobile app",
    sso: "SSO / audience gating",
    evaluations: "Evaluations",
    remove_branding: "Remove branding included",
    custom_roles: "Custom roles",
    private_deployment: "Private / on-prem deployment",
    sla: "99.9% SLA + DPA",
    customer_success_manager: "Customer success manager",
    after_sales: "After-sales (assets, visits, RMA)",
    commerce: "Catalogue and orders",
    commerce_presets: "Vendor connector presets",
    connectors: "API connectors",
    graph_retrieval: "Knowledge graph (Experiment)",
    secure_launch: "Secure launch (signed-in chat)",
    secure_launch_facts: "Secure launch customer facts",
  },
  editor: {
    saved: "{{plan}} saved",
    pricesHint: "Prices live in the price book: use “Add price” on the plan card (per currency, with grandfathering).",
    editTitle: "Edit plan · {{plan}}",
    newTitle: "New plan",
    subscribers_one: "{{count}} active subscriber",
    subscribers_other: "{{count}} active subscribers",
    newDescription: "Saved as a draft, hidden from the pricing page until you publish it.",
    name: "Plan name",
    key: "Plan key",
    keyHint: "Lowercase identifier used in the API and billing; it can't be changed later.",
    keyInvalid: "Use 2–64 lowercase letters, numbers, dots or underscores, starting with a letter.",
    nameRequired: "Give the plan a name (up to 200 characters).",
    tagline: "Tagline",
    limits: "Limits",
    unlimitedPlaceholder: "Unlimited",
    messages: "Messages / month",
    agents: "Agents",
    storage: "Storage (MB)",
    seats: "Seats",
    fileSize: "Max file size (MB)",
    rateLimit: "Rate limit (req/min)",
    invalidLimit: "Enter a whole number or leave blank for unlimited.",
    invalidFile: "Enter a whole number from 1 to {{max}}.",
    models: "Allowed model tiers",
    tiersRequired: "Allow at least one model tier.",
    features: "Features",
    visibility: "Pricing page",
    popular: "Mark as “Most popular”",
    visible: "Show on the pricing page",
    save: "Save plan",
    create: "Create plan",
    created: "{{plan}} plan created (draft — hidden from pricing page)",
  },
  addons: {
    noPrice: "No price yet",
    addPrice: "Add a price for {{addon}}",
    title: "Add-ons",
    subtitle: "Sold on top of any paid plan",
    add: "Add-on",
    colAddon: "Add-on",
    colType: "Type",
    colPrice: "Price",
    colAvailable: "Available on",
    colStatus: "Status",
    colActions: "Actions",
    types: { recurring: "Recurring", one_time: "One-time", setup_fee: "Setup fee" },
    pricePerMonth: "{{price}}/mo",
    priceSetup: "{{price}} setup",
    credits: "+{{count}} message credits",
    allPlans: "All plans",
    noPlans: "No plans",
    statusLabel: "Show {{addon}} at checkout",
    shown: "{{addon}} shown at checkout",
    hidden: "{{addon}} hidden from checkout",
    edit: "Edit {{addon}}",
    emptyTitle: "No add-ons yet",
    emptyBody: "Add-ons let tenants buy extra messages, agents or seats on top of their plan.",
  },
  addonDialog: {
    pricesHint: "Set prices per currency with the price button in the add-ons table.",
    newTitle: "New add-on",
    editTitle: "Edit add-on",
    name: "Name",
    namePlaceholder: "e.g. Priority support (4-hr response)",
    nameRequired: "Give the add-on a name.",
    description: "Description",
    descriptionPlaceholder: "Shown under the name at checkout",
    billing: "Billing",
    billingTypes: { recurring: "Recurring monthly", one_time: "One-time", setup_fee: "Setup fee" },
    credits: "Message credits",
    creditsHint: "Optional: credits added per purchase.",
    creditsInvalid: "Enter a whole number or leave blank.",
    available: "Available on",
    availableRequired: "Pick at least one plan.",
    save: "Save add-on",
    saved: "Add-on saved",
  },
  priceDialog: {
    title: "New price · {{item}}",
    body: "Prices are never edited: a new row starts on its date. Issued invoices keep their prices.",
    currency: "Currency",
    interval: "Billing interval",
    amount: "Amount (before tax)",
    amountInvalid: "Enter an amount with up to 2 decimals.",
    effectiveFrom: "Effective from",
    effectiveHint: "Today or later.",
    dateInvalid: "Pick today or a later date.",
    grandfather: "Grandfather current subscribers",
    grandfatherOn: "The {{count}} subscribers on this plan, currency and interval keep their current price until they change plans.",
    grandfatherOff: "The {{count}} subscribers on this plan, currency and interval move to this price at their next renewal.",
    save: "Add price",
    saved: "New price added for {{item}}",
    savedGrandfathered_one: "{{price}} added for {{item}} · {{count}} subscriber keeps the old price",
    savedGrandfathered_other: "{{price}} added for {{item}} · {{count}} subscribers keep the old price",
    savedMoving_one: "{{price}} added for {{item}} · {{count}} subscriber moves at next renewal",
    savedMoving_other: "{{price}} added for {{item}} · {{count}} subscribers move at next renewal",
  },
  tax: {
    title: "Tax rules",
    subtitle: "Per billing country; the latest rule from its date applies to new invoices.",
    country: "Country",
    countryHint: "ISO code, or * for every other country",
    countryInvalid: "Use a 2-letter country code or *.",
    mode: "Treatment",
    modes: { exclusive: "Added on top", inclusive: "Included in price", zero_rated: "Zero-rated (0%)", exempt: "Exempt" },
    rate: "Rate (%)",
    rateInvalid: "Enter a percentage below 100.",
    label: "Invoice label",
    labelRequired: "Give the rule an invoice label.",
    note: "Invoice note",
    add: "Add rule",
    saved: "Tax rule for {{country}} added",
    everyOther: "Every other country",
  },
  coupons: {
    title: "Coupons",
    create: "Create coupon",
    colCode: "Code",
    colDiscount: "Discount",
    colUses: "Uses",
    colExpires: "Expires",
    colStatus: "Status",
    colActions: "Actions",
    copy: "Copy code {{code}}",
    copied: "Coupon code copied",
    discount: { pct: "{{value}}% off", fixed: "{{value}} off", trial_days: "+{{value}} trial days" },
    durations: { once: "once", "3m": "× 3 months", "6m": "× 6 months", forever: "forever", annual: "annual" },
    uses: "{{used}} / {{max}}",
    states: { active: "Active", expired: "Expired", disabled: "Disabled", used_up: "Fully redeemed" },
    disable: "Disable",
    disableLabel: "Disable coupon {{code}}",
    disabledToast: "Coupon {{code}} disabled",
    emptyTitle: "No coupons yet",
    emptyBody: "Create a code for a campaign, partner or pilot discount.",
  },
  couponDialog: {
    currency: "Currency",
    currencyHint: "Fixed amounts apply only to invoices in this currency.",
    title: "Create coupon",
    code: "Code",
    codeInvalid: "Use 3–40 letters, numbers, dashes or underscores.",
    generate: "Generate",
    type: "Type",
    types: { pct: "Percent off", fixed: "Fixed amount", trial_days: "Extra trial days" },
    value: "Value",
    valueInvalid: "Enter a value greater than 0.",
    pctInvalid: "A percentage can't exceed 100.",
    duration: "Duration",
    durations: { once: "Once", "3m": "3 months", "6m": "6 months", forever: "Forever", annual: "Annual billing" },
    max: "Max redemptions",
    maxPlaceholder: "Unlimited",
    maxInvalid: "Enter a whole number of at least 1, or leave blank.",
    expires: "Expires",
    appliesTo: "Applies to",
    allPlans: "All plans",
    newOnly: "New customers only",
    submit: "Create coupon",
    created: "Coupon {{code}} created",
  },
  gateways: {
    paypal: "PayPal",
    currencies: "Collects {{currencies}}",
    adapterModes: { live: "Live", test: "Test mode", mock: "Mock (no keys)", unconfigured: "Not configured" },
    publicKeyOf: "{{gateway}} public identifier",
    secretOk: "Secret key configured",
    secretMissing: "Secret key missing",
    env: "Environment: {{env}}",
    title: "Payment gateways & receipts",
    subtitle: "Checkout methods shown to tenants and BIR-compliant OR numbering",
    settingsLink: "Platform settings",
    connected: "Connected",
    notConfigured: "Not configured",
    paymongo: "PayMongo",
    paymongoMeta: "PHP · primary · {{mode}} mode",
    modes: { live: "live", test: "test" },
    methodsLabel: "PayMongo checkout methods",
    methods: { gcash: "GCash", maya: "Maya", cards: "Cards (Visa / Mastercard)", online_banking: "Online banking (BPI, UnionBank)" },
    methodOn: "{{method}} enabled",
    methodOff: "{{method}} hidden at checkout",
    publicKey: "PayMongo public key",
    keyMissing: "No public key configured",
    webhookOk: "Webhook secret configured",
    webhookMissing: "Webhook secret missing",
    stripe: "Stripe",
    stripeMeta: "USD · card checkout",
    lockFx: "Lock FX rate monthly",
    lockOn: "FX rate locked monthly",
    lockOff: "FX rate follows daily BSP reference",
  },
  orSeries: {
    title: "BIR official receipts",
    prefix: "OR prefix",
    prefixInvalid: "1–12 uppercase letters, numbers or dashes.",
    next: "Next number",
    start: "Series start",
    end: "Series end",
    numberInvalid: "Enter a whole number of at least 1.",
    rangeInvalid: "Series end must be after the start.",
    nextInvalid: "Next number must be inside the series.",
    atp: "ATP / CAS permit no.",
    registrantName: "Registered name",
    registrantTin: "TIN",
    registrantAddress: "Registered address",
    remaining_one: "{{count}} receipt remaining.",
    remaining_other: "{{count}} receipts remaining.",
    registeredTo: "Registered to {{line}}.",
    none: "No receipt series yet — set one up before issuing official receipts.",
    save: "Save series",
    create: "Create series",
    saved: "BIR receipt series saved",
  },
} as const;
